import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import publicApi from '../helper/publicApi'
import { useLocation } from '../context/LocationContext'

const Contact = () => {
    const { isCasaRegion } = useLocation();

    // Get cached settings from localStorage immediately
    const getInitialSettings = () => {
        try {
            const cached = localStorage.getItem('siteSettings');
            if (cached) {
                return JSON.parse(cached);
            }
        } catch (e) {
            console.error('Error reading cache:', e);
        }
        return null;
    };

    const [settings, setSettings] = useState(getInitialSettings);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await publicApi.getSettings();
                if (response.success) {
                    setSettings(response.data);
                    localStorage.setItem('siteSettings', JSON.stringify(response.data));
                }
            } catch (error) {
                console.error('Error fetching contact settings:', error);
            }
        };
        fetchSettings();
    }, []);

    // Branch addresses based on location (only addresses change, not phone/email)
    const branchFes1Address = settings?.branch_fes1_address || '36 Avenue Ibn Khatib, Fès';
    const branchFes2Address = settings?.branch_fes2_address || '';
    const branchCasaAddress = settings?.branch_casa_address || '';

    const addressesToShow = isCasaRegion
        ? (branchCasaAddress ? [branchCasaAddress] : [])
        : [branchFes1Address, branchFes2Address].filter(Boolean);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        website: ''
    });
    const formStartedAt = useRef(Date.now());
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [id]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(false);

        try {
            const response = await publicApi.post('/public/contact', {
                ...formData,
                form_time: Date.now() - formStartedAt.current,
            });
            if (response.data.success) {
                setSuccess(true);
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    subject: '',
                    message: '',
                    website: ''
                });
            }
        } catch (err) {
            if (err.response?.status === 429) {
                setError('Trop de messages envoyés. Veuillez réessayer dans quelques minutes.');
            } else {
                setError(err.response?.data?.message || 'Une erreur est survenue. Veuillez réessayer.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="contact py-80">
            <div className="container container-lg">
                <div className="row gy-5">
                    <div className="col-lg-8">
                        <div className="contact-box border border-gray-100 rounded-16 px-24 py-40">
                            <form onSubmit={handleSubmit}>
                                <h6 className="mb-32">Faire une demande personnalisée</h6>
                                
                                {success && (
                                    <div className="alert bg-success-100 text-success-600 rounded-8 p-16 mb-24 flex-align gap-12">
                                        <i className="ph ph-check-circle text-2xl"></i>
                                        <span>Votre message a été envoyé avec succès. Nous vous répondrons dans les plus brefs délais.</span>
                                    </div>
                                )}
                                
                                {error && (
                                    <div className="alert bg-danger-100 text-danger-600 rounded-8 p-16 mb-24 flex-align gap-12">
                                        <i className="ph ph-warning text-2xl"></i>
                                        <span>{error}</span>
                                    </div>
                                )}

                                <div
                                    aria-hidden="true"
                                    style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}
                                >
                                    <label htmlFor="website">Website</label>
                                    <input
                                        type="text"
                                        id="website"
                                        name="website"
                                        tabIndex={-1}
                                        autoComplete="off"
                                        value={formData.website}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="row gy-4">
                                    <div className="col-sm-6 col-xs-6">
                                        <label
                                            htmlFor="name"
                                            className="flex-align gap-4 text-sm font-heading-two text-gray-900 fw-semibold mb-4"
                                        >
                                            Nom complet{" "}
                                            <span className="text-danger text-xl line-height-1">*</span>{" "}
                                        </label>
                                        <input
                                            type="text"
                                            className="common-input contact-form-input px-16"
                                            id="name"
                                            placeholder="Nom complet"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                            disabled={loading}
                                        />
                                    </div>
                                    <div className="col-sm-6 col-xs-6">
                                        <label
                                            htmlFor="email"
                                            className="flex-align gap-4 text-sm font-heading-two text-gray-900 fw-semibold mb-4"
                                        >
                                            Adresse Email{" "}
                                            <span className="text-danger text-xl line-height-1">*</span>{" "}
                                        </label>
                                        <input
                                            type="email"
                                            className="common-input contact-form-input px-16"
                                            id="email"
                                            placeholder="Adresse email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                            disabled={loading}
                                        />
                                    </div>
                                    <div className="col-sm-6 col-xs-6">
                                        <label
                                            htmlFor="phone"
                                            className="flex-align gap-4 text-sm font-heading-two text-gray-900 fw-semibold mb-4"
                                        >
                                            Numéro de téléphone
                                        </label>
                                        <input
                                            type="tel"
                                            className="common-input contact-form-input px-16"
                                            id="phone"
                                            placeholder="Numéro de téléphone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            disabled={loading}
                                        />
                                    </div>
                                    <div className="col-sm-6 col-xs-6">
                                        <label
                                            htmlFor="subject"
                                            className="flex-align gap-4 text-sm font-heading-two text-gray-900 fw-semibold mb-4"
                                        >
                                            Sujet
                                            <span className="text-danger text-xl line-height-1">*</span>{" "}
                                        </label>
                                        <input
                                            type="text"
                                            className="common-input contact-form-input px-16"
                                            id="subject"
                                            placeholder="Sujet de votre message"
                                            value={formData.subject}
                                            onChange={handleChange}
                                            required
                                            disabled={loading}
                                        />
                                    </div>
                                    <div className="col-sm-12">
                                        <label
                                            htmlFor="message"
                                            className="flex-align gap-4 text-sm font-heading-two text-gray-900 fw-semibold mb-4"
                                        >
                                            Message
                                            <span className="text-danger text-xl line-height-1">*</span>{" "}
                                        </label>
                                        <textarea
                                            className="common-input contact-form-input px-16"
                                            id="message"
                                            placeholder="Tapez votre message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            required
                                            disabled={loading}
                                            rows={5}
                                        />
                                    </div>
                                    <div className="col-sm-12 mt-32">
                                        <button
                                            type="submit"
                                            className="btn track-order-btn py-18 px-32 rounded-8 flex-align gap-8"
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm"></span>
                                                    Envoi en cours...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="ph ph-paper-plane-right"></i>
                                                    Envoyer
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                    <div className="col-lg-4">
                        <div className="contact-box border border-gray-100 rounded-16 px-24 py-40">
                            <h6 className="mb-48">Nos Adresses</h6>
                            <div className="flex-align gap-16 mb-16">
                                <span className="w-40 h-40 flex-center rounded-circle border border-gray-100 contact-address-icon text-2xl flex-shrink-0">
                                    <i className="ph-fill ph-phone-call" />
                                </span>
                                <div className="flex-align gap-8 flex-wrap">
                                    <Link
                                        to="/tel:0661700052"
                                        className="text-md text-gray-900 hover-text-main-600"
                                    >
                                        06 61 70 00 52
                                    </Link>
                                    <span className="text-md text-gray-500">|</span>
                                    <Link
                                        to="/tel:0666944224"
                                        className="text-md text-gray-900 hover-text-main-600"
                                    >
                                        06 66 94 42 24
                                    </Link>
                                </div>
                            </div>
                            <div className="flex-align gap-16 mb-16">
                                <span className="w-40 h-40 flex-center rounded-circle border border-gray-100 contact-address-icon text-2xl flex-shrink-0">
                                    <i className="ph-fill ph-envelope" />
                                </span>
                                <Link
                                    to="/mailto:contact@delicesbf.com"
                                    className="text-md text-gray-900 hover-text-main-600"
                                >
                                    contact@delicesbf.com
                                </Link>
                            </div>
                            <div className="flex-align gap-16 mb-16">
                                <span className="w-40 h-40 flex-center rounded-circle border border-gray-100 contact-address-icon text-2xl flex-shrink-0">
                                    <i className="ph-fill ph-map-pin" />
                                </span>
                                <div className="flex-align gap-8 flex-wrap">
                                    {addressesToShow.length > 0 ? (
                                        addressesToShow.map((address, index) => (
                                            <React.Fragment key={address}>
                                                {index > 0 && <span className="text-md text-gray-500">|</span>}
                                                <span className="text-md text-gray-900">{address}</span>
                                            </React.Fragment>
                                        ))
                                    ) : (
                                        <span className="text-md text-gray-900">{branchFes1Address}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="mt-24 flex-align flex-wrap gap-16">
                            <div
                                className="contact-hours-box rounded-8 p-10 px-16 flex-between flex-wrap gap-8 flex-grow-1"
                            >
                                <span className="text-white fw-medium">Du Lundi au Samedi : 9h00 - 18h00</span>
                                <span className="w-36 h-36 contact-hours-icon rounded-8 flex-center text-xl">
                                    <i className="ph ph-headset" />
                                </span>
                            </div>
                            <div
                                className="contact-hours-box rounded-8 p-10 px-16 flex-between flex-wrap gap-8 flex-grow-1"
                            >
                                <span className="text-white fw-medium">Dimanche : 10h00 - 14h00</span>
                                <span className="w-36 h-36 contact-hours-icon rounded-8 flex-center text-xl">
                                    <i className="ph ph-map-pin" />
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

    )
}

export default Contact